import { createFileRoute, Link } from "@tanstack/react-router";
import { aeoGeo, breadcrumbs } from "@/lib/seo/schema";
import {
  CORE_PAGES,
  SERVICE_PAGES,
  REGION_HUBS,
  LOCATION_PAGES,
  EN_PAGES,
} from "@/lib/seo/sitemap-catalog";
import { primaryPathFor } from "@/lib/seo/canonical-overrides";

const TITLE = "Alla sidor på fenny.se — sidkarta | Fenny";
const DESCRIPTION =
  "Sidkarta över hela fenny.se: filmproduktion, orter, tjänster, jämförelser, kunskap och engelska sidor. Hitta rätt sida på ett par sekunder.";

/** Gör en läsbar rubrik av en sökväg: /filmbyra/boras → "Filmbyrå Borås". */
const WORD_FIX: Record<string, string> = {
  filmbyra: "Filmbyrå",
  videobyra: "Videobyrå",
  goteborg: "Göteborg",
  malmo: "Malmö",
  boras: "Borås",
  gavle: "Gävle",
  orebro: "Örebro",
  vasteras: "Västerås",
  vaxjo: "Växjö",
  jonkoping: "Jönköping",
  linkoping: "Linköping",
  norrkoping: "Norrköping",
  lulea: "Luleå",
  umea: "Umeå",
  ostersund: "Östersund",
  skelleftea: "Skellefteå",
  molndal: "Mölndal",
  sodertalje: "Södertälje",
  sodermalm: "Södermalm",
  taby: "Täby",
  nykoping: "Nyköping",
  borlange: "Borlänge",
  skovde: "Skövde",
  trollhattan: "Trollhättan",
  dronarfilm: "Drönarfilm",
  dronarfoto: "Drönarfoto",
  fragor: "Frågor",
  jamfor: "Jämför",
  priser: "Priser",
  foto: "Foto",
  en: "EN",
};

function labelFor(path: string): string {
  const parts = path.replace(/^\//, "").split("/");
  return parts
    .map((part) =>
      part
        .split("-")
        .map((word) => WORD_FIX[word] ?? word)
        .join(" "),
    )
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" — ");
}

type Group = { heading: string; paths: string[] };

function dedupe(paths: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const path of paths) {
    // Stödsidor som pekar canonical vidare listas inte separat.
    if (primaryPathFor(path) !== path) continue;
    if (seen.has(path)) continue;
    seen.add(path);
    out.push(path);
  }
  return out.sort((a, b) => a.localeCompare(b, "sv"));
}

const corePaths = CORE_PAGES.map((p) => p.path);
const servicePaths = SERVICE_PAGES.map((p) => p.path);
const locationPaths = [...REGION_HUBS, ...LOCATION_PAGES].map((p) => p.path);
const enPaths = EN_PAGES.map((p) => p.path);

const GROUPS: Group[] = [
  {
    heading: "Start och företaget",
    paths: dedupe(corePaths.filter((p) => !p.startsWith("/jamfor") && !p.startsWith("/fragor"))),
  },
  {
    heading: "Jämförelser och beslutsunderlag",
    paths: dedupe(corePaths.filter((p) => p.startsWith("/jamfor"))),
  },
  {
    heading: "Frågor och svar",
    paths: dedupe(corePaths.filter((p) => p.startsWith("/fragor"))),
  },
  {
    heading: "Tjänster — film",
    paths: dedupe(servicePaths.filter((p) => !p.startsWith("/foto"))),
  },
  { heading: "Orter och regioner", paths: dedupe(locationPaths) },
  { heading: "In English", paths: dedupe(enPaths) },
];

export const Route = createFileRoute("/alla-sidor")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/alla-sidor" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/alla-sidor" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          aeoGeo({ path: "/alla-sidor", name: TITLE, description: DESCRIPTION }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Alla sidor", path: "/alla-sidor" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  const total = GROUPS.reduce((sum, group) => sum + group.paths.length, 0);
  return (
    <main className="mx-auto w-full max-w-6xl px-5 py-16 md:py-24">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Sidkarta</p>
      <h1 className="mt-3 text-3xl font-semibold md:text-5xl">Alla sidor på fenny.se</h1>
      <p className="mt-4 max-w-2xl text-muted-foreground">
        {total} sidor om filmproduktion, priser och orter. Letar ni efter något
        specifikt går det snabbast att{" "}
        <Link to="/kontakt" className="underline hover:text-mint">
          höra av er direkt
        </Link>
        .
      </p>

      <div className="mt-12 space-y-12">
        {GROUPS.filter((group) => group.paths.length > 0).map((group) => (
          <section key={group.heading}>
            <h2 className="text-lg font-semibold">{group.heading}</h2>
            <ul className="mt-4 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-3">
              {group.paths.map((path) => (
                <li key={path}>
                  <Link
                    to={path}
                    className="text-muted-foreground underline-offset-4 hover:text-mint hover:underline"
                  >
                    {labelFor(path)}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </main>
  );
}
